import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ruler, Palette, Truck, Layers, Box, CheckCircle2, ShieldCheck, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const features = [
    {
      icon: Ruler,
      title: 'Individuelle Größen',
      description: 'Von 500mm bis 1800mm Durchmesser - präzise nach Ihren Anforderungen.',
    },
    {
      icon: Palette,
      title: 'Freie Farbwahl',
      description: 'Homogene Lackierung in jeder gewünschten Farbe möglich.',
    },
    {
      icon: ShieldCheck,
      title: 'DIN 4102 B1 Zertifiziert',
      description: 'Schwer entflammbares Grundmaterial. Optional A1-Beschichtung verfügbar.',
    },
    {
      icon: Truck,
      title: 'Schnelle Lieferung',
      description: 'Zuverlässige Lieferung zu Ihrem Wunschtermin.',
    },
  ];

  const options = [
    { label: 'Natur (unlackiert)', icon: Box },
    { label: '2 Halbkugeln', icon: Layers },
    { label: 'Mit Abhängung', icon: CheckCircle2 },
    { label: 'Abgeflacht', icon: CheckCircle2 },
    { label: 'Mit Unterlegring', icon: CheckCircle2 },
    { label: 'A1-Beschichtung (nicht brennbar)', icon: Flame },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-8">
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Content */}
            <div className="opacity-0 animate-fade-in">
              <p className="text-label text-primary mb-4">Styropor®kugeln nach Maß</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight mb-6">
                Präzision in
                <br />
                <span className="text-primary">Perfektion</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-400 max-w-lg mb-8 leading-relaxed">
                Konfigurieren Sie Ihre individuellen Styropor®kugeln für Events, 
                Dekoration, Werbung und mehr. Von 500mm bis 1800mm, in jeder Farbe.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/configurator">
                  <Button 
                    className="h-12 px-8 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90 btn-active"
                    data-testid="hero-cta-button"
                  >
                    Jetzt konfigurieren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/cart">
                  <Button 
                    variant="outline"
                    className="h-12 px-8 border border-zinc-700 hover:border-primary hover:text-primary bg-transparent text-white uppercase tracking-wider rounded-sm"
                    data-testid="hero-inquiry-button"
                  >
                    Anfrage stellen
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right: Sphere Visual */}
            <div className="relative flex items-center justify-center opacity-0 animate-fade-in stagger-2">
              <div className="relative">
                {/* Glow */}
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl scale-150" />
                
                {/* Main Sphere */}
                <div 
                  className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full bg-white sphere-shadow animate-pulse-glow"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #ffffff 0%, #e0e0e0 50%, #a0a0a0 100%)',
                  }}
                >
                  {/* Highlight */}
                  <div className="absolute top-[10%] left-[15%] w-[25%] h-[25%] rounded-full bg-white/80 blur-sm" />
                </div>
                
                {/* Size indicator */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center">
                  <span className="text-label">500 - 1800 mm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 opacity-0 animate-fade-in">
            <p className="text-label text-primary mb-4">Unsere Leistungen</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
              Alles aus einer Hand
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm hover:border-zinc-700 transition-all duration-300 hover-lift opacity-0 animate-fade-in stagger-${index + 1}`}
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold uppercase tracking-tight text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Options Section */}
      <section className="py-24 md:py-32 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="opacity-0 animate-fade-in">
              <p className="text-label text-primary mb-4">Vielfältige Möglichkeiten</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                Optionen nach Ihren Wünschen
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Ob für Bühnenbilder, Messestände, Schaufenster oder Kunstinstallationen - 
                unsere Styroporkugeln lassen sich perfekt an Ihre Anforderungen anpassen.
              </p>
              
              <ul className="space-y-4">
                {options.map((option, index) => (
                  <li 
                    key={option.label}
                    className={`flex items-center gap-3 text-zinc-300 opacity-0 animate-slide-in stagger-${index + 1}`}
                  >
                    <option.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>{option.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative opacity-0 animate-fade-in stagger-3">
              <div className="grid grid-cols-2 gap-4">
                {/* Preview cards showing different options */}
                <div className="bg-zinc-800/50 border border-zinc-700 p-6 rounded-sm aspect-square flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-zinc-200 sphere-shadow" />
                </div>
                <div className="bg-zinc-800/50 border border-zinc-700 p-6 rounded-sm aspect-square flex items-center justify-center">
                  <div className="flex gap-1">
                    <div className="w-10 h-20 bg-zinc-200 sphere-shadow" style={{ borderRadius: '40px 0 0 40px' }} />
                    <div className="w-10 h-20 bg-zinc-200 sphere-shadow" style={{ borderRadius: '0 40px 40px 0' }} />
                  </div>
                </div>
                <div className="bg-zinc-800/50 border border-zinc-700 p-6 rounded-sm aspect-square flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    <div className="w-0.5 h-6 bg-zinc-500" />
                    <div className="w-16 h-16 rounded-full bg-primary/80 sphere-shadow" />
                  </div>
                </div>
                <div className="bg-zinc-800/50 border border-zinc-700 p-6 rounded-sm aspect-square flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-12 bg-blue-400 sphere-shadow" style={{ borderRadius: '40px 40px 0 0' }} />
                    <div className="w-12 h-2 bg-zinc-600 rounded-sm" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-6 opacity-0 animate-fade-in">
            Bereit für Ihre <span className="text-primary">individuelle Kugel</span>?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto opacity-0 animate-fade-in stagger-1">
            Starten Sie jetzt mit der Konfiguration und erhalten Sie ein unverbindliches Angebot für Ihre maßgefertigten Styropor®kugeln.
          </p>
          <Link to="/configurator" className="opacity-0 animate-fade-in stagger-2 inline-block">
            <Button 
              className="h-14 px-10 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90 btn-active text-lg"
              data-testid="cta-configure-button"
            >
              Konfigurator starten
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-black" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-tight text-zinc-400">
                Styroporkugeln24
              </span>
              <span className="text-xs text-zinc-500 block normal-case">powered by Styroporzuschnitte24</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/legal" className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
                Impressum & AGB
              </Link>
              <p className="text-sm text-zinc-500">
                © {new Date().getFullYear()} Styroporkugeln24
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
